import { Button } from '@1trading/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const MAX_PLAIN_PAGES = 7;
const PAGE_WINDOW_RADIUS = 1;

type PageItem = number | `gap-after-${number}`;

// Windowed page list: 1 … around current … total (totalPages only ever grows by
// one while walking forward, but a long walk should not render dozens of buttons)
export const getPageItems = (current: number, total: number): PageItem[] => {
  if (total <= MAX_PLAIN_PAGES) return Array.from({ length: total }, (_, index) => index + 1);
  const pages = new Set<number>([1, total]);
  for (let page = current - PAGE_WINDOW_RADIUS; page <= current + PAGE_WINDOW_RADIUS; page += 1) {
    if (page >= 1 && page <= total) pages.add(page);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  return sorted.flatMap((page, index) => {
    const previous = sorted[index - 1];
    return index > 0 && previous !== undefined && page - previous > 1 ? [`gap-after-${previous}` as const, page] : [page];
  });
};

export interface PaginationState {
  currentPage: number;
  totalPages: number;
  /** page number → opaque cursor that fetches it; page 1 never has one */
  cursors: Record<number, string>;
}

export const INITIAL_PAGINATION: PaginationState = { currentPage: 1, totalPages: 1, cursors: {} };

// Cursor-walk bookkeeping. The page count grows only when a page is DISCOVERED — a refetch
// of an already-visited page that returns a different cursor (data moved under us) replaces
// the stored cursor without minting a phantom extra page.
export const registerNextPageCursor = (previous: PaginationState, nextCursor: string): PaginationState => {
  const nextPage = previous.currentPage + 1;
  if (previous.cursors[nextPage] === nextCursor) return previous;
  const isNewPage = previous.cursors[nextPage] === undefined;
  return {
    ...previous,
    totalPages: isNewPage ? Math.max(previous.totalPages, nextPage) : previous.totalPages,
    cursors: { ...previous.cursors, [nextPage]: nextCursor },
  };
};

interface CursorPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Names the nav landmark for screen readers, e.g. "Transaction pages" */
  ariaLabel?: string;
}

/**
 * Cursor-masquerading-as-pages control: the wire protocol is cursor-based, so exactly one
 * page beyond the highest visited page is shown while more data exists — the true total is
 * never known.
 */
export const CursorPagination = ({ currentPage, totalPages, onPageChange, ariaLabel = 'Pages' }: CursorPaginationProps) => (
  <nav aria-label={ariaLabel} className="flex items-center gap-1">
    <Button aria-label="Previous page" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} size="icon" type="button" variant="outline">
      <ChevronLeft className="size-4" />
    </Button>
    {getPageItems(currentPage, totalPages).map((item) =>
      typeof item === 'number' ? (
        <Button
          aria-current={item === currentPage ? 'page' : undefined}
          key={item}
          onClick={() => onPageChange(item)}
          size="icon"
          type="button"
          variant={item === currentPage ? 'default' : 'outline'}
        >
          {item}
        </Button>
      ) : (
        <span className="px-1 text-muted-foreground" key={item}>
          …
        </span>
      ),
    )}
    <Button aria-label="Next page" disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)} size="icon" type="button" variant="outline">
      <ChevronRight className="size-4" />
    </Button>
  </nav>
);
CursorPagination.displayName = 'CursorPagination';
